import Swiper from "swiper/bundle";

function controlAutoplayVideos(swiper) {
  if (!swiper || !swiper.slides) return;
  const slides = swiper.slides;
  
  const rect = swiper.el.getBoundingClientRect();
  const inViewport = (
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );

  slides.forEach((slide) => {
    const video = slide.querySelector("video");
    if (!video) return;

    const isSlideVisible = slide.classList.contains("swiper-slide-visible") || slide.classList.contains("swiper-slide-active");

    if (inViewport && isSlideVisible) {
      if (video.paused) {
        video.play().catch(() => {});
      }
    } else {
      if (!video.paused) {
        video.pause();
      }
    }
  });
}

export function initPopularProductsTabs(scope = document) {
  const sections = scope.querySelectorAll("[data-popular-section]");
  if (!sections.length) return;

  sections.forEach((section) => {
    const tabs = section.querySelectorAll(".popular-products__tab");
    const panels = section.querySelectorAll("[data-popular-root]");

    function destroySwiper(panel) {
      const el = panel.querySelector(".js-popular-swiper");
      if (el) {
        if (el._observer) {
          el._observer.disconnect();
          el._observer = null;
        }
        if (el._swiper) {
          el._swiper.destroy(true, true);
          el._swiper = null;
        }
      }
    }

    function initSwiper(panel) {
      const root = panel;
      const el = root.querySelector(".js-popular-swiper");
      const fill = root.querySelector(".js-popular-fill");
      if (!el) return;

      if (el._observer) {
        el._observer.disconnect();
        el._observer = null;
      }
      if (el._swiper) el._swiper.destroy(true, true);

      let data = {};
      try {
        data = JSON.parse(el.getAttribute("data-options") || "{}");
      } catch (_) {}

      el._swiper = new Swiper(el, {
        watchSlidesProgress: true,
        ...data,
        navigation: {
          nextEl: root.querySelector(".popular-next"),
          prevEl: root.querySelector(".popular-prev"),
        },
        on: {
          init() {
            updateFill(this, fill);
            controlAutoplayVideos(this);
          },
          setTranslate() {
            updateFill(this, fill);
          },
          transitionEnd() {
            updateFill(this, fill);
            controlAutoplayVideos(this);
          },
          slideChange() {
            controlAutoplayVideos(this);
          }
        },
      });

      if (window.IntersectionObserver) {
        el._observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (el._swiper) {
              controlAutoplayVideos(el._swiper);
            }
          });
        }, { threshold: 0.05 });
        el._observer.observe(section);
      }

      function updateFill(sw, fillEl) {
        if (!fillEl) return;
        const p = sw.progress;
        const clamped = typeof p === 'number' ? Math.max(0, Math.min(1, p)) : 0;
        fillEl.style.transform = `scaleX(${0.2 + clamped * 0.8})`;
      }
    }

    function setActiveTab(targetPanelId) {
      tabs.forEach((btn) => {
        const isActive = btn.getAttribute("data-tab-target") === targetPanelId;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      panels.forEach((panel) => {
        const isActive = panel.id === targetPanelId;

        if (isActive) {
          panel.classList.add("is-active");
          panel.setAttribute("data-active", "true");
          initSwiper(panel);
          requestAnimationFrame(() => {
            const el = panel.querySelector(".js-popular-swiper");
            if (el && el._swiper) el._swiper.update();
          });
        } else {
          panel.classList.remove("is-active");
          panel.setAttribute("data-active", "false");
          destroySwiper(panel);
        }
      });
    }

    function getQueryParam(name) {
      const url = new URL(window.location.href);
      return url.searchParams.get(name);
    }

    function escapeHtml(str = "") {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    function formatMoney(cents) {
      if (typeof cents !== "number") cents = 0;
      const isFrench = (document.documentElement.lang || window.Shopify?.locale || "").toLowerCase().startsWith("fr");
      const value = (cents / 100).toFixed(2).replace(".", ",");
      if (isFrench) {
        return `${value} €`;
      }
      return `€${value}`.replace(",00", ",-");
    }

    async function fetchProductByHandle(handle) {
      if (!handle) return null;

      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        console.error("Error loading product by handle:", handle, error);
        return null;
      }
    }

    async function fetchRecommendations(productId, limit = 8, intent = "related") {
      if (!productId) return [];

      try {
        const response = await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`
        );

        if (!response.ok) return [];

        const data = await response.json();
        return Array.isArray(data?.products) ? data.products : [];
      } catch (error) {
        console.error("Error loading recommendations:", error);
        return [];
      }
    }

    async function fetchCart() {
      try {
        const response = await fetch("/cart.js");
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        console.error("Error loading cart:", error);
        return null;
      }
    }

    function getProductImage(product) {
      if (!product) return "";

      if (typeof product.featured_image === "string") return product.featured_image;
      if (product.featured_image?.url) return product.featured_image.url;
      if (product.featured_image?.src) return product.featured_image.src;

      if (Array.isArray(product.images) && product.images.length) {
        const firstImage = product.images[0];
        if (typeof firstImage === "string") return firstImage;
        if (firstImage?.src) return firstImage.src;
        if (firstImage?.url) return firstImage.url;
      }

      return "";
    }

    function getProductUrl(product) {
      if (product?.url) return product.url;
      if (product?.handle) return `/products/${product.handle}`;
      return "#";
    }

    function getFirstAvailableVariant(product) {
      if (!product?.variants?.length) return null;
      return product.variants.find((variant) => variant.available) || product.variants[0] || null;
    }

    function uniqueProducts(products = []) {
      const seen = new Map();

      products.forEach((product) => {
        if (!product?.handle) return;
        if (!seen.has(product.handle)) {
          seen.set(product.handle, product);
        }
      });

      return Array.from(seen.values());
    }

    function buildDynamicRatingHtml(product, panel, uniqueId) {
      // 1. Extract reviews_rating with fallback to 5.0
      let rating = 5.0;
      if (product) {
        const rawRating = product.metafields?.custom?.reviews_rating;
        const val = typeof rawRating === 'object' && rawRating !== null ? rawRating.value : rawRating;
        if (val !== undefined && val !== null) {
          const parsed = parseFloat(val);
          if (!isNaN(parsed)) rating = parsed;
        }
      }

      // 2. Fallbacks from panel.dataset.reviewsText
      let defaultCount = 676;
      let defaultText = "beoordelingen";
      if (panel && panel.dataset.reviewsText) {
        const cleanText = panel.dataset.reviewsText.trim();
        const matches = cleanText.match(/^(\d+)\s*(.*)$/);
        if (matches) {
          defaultCount = parseInt(matches[1], 10) || 676;
          defaultText = matches[2] || "beoordelingen";
        } else {
          defaultText = cleanText;
        }
      }

      // 3. Extract reviews_count with fallback to defaultCount
      let count = defaultCount;
      if (product) {
        const rawCount = product.metafields?.custom?.reviews_count;
        const val = typeof rawCount === 'object' && rawCount !== null ? rawCount.value : rawCount;
        if (val !== undefined && val !== null) {
          const parsed = parseInt(val, 10);
          if (!isNaN(parsed)) count = parsed;
        }
      }

      // 4. Extract reviews_text with fallback to defaultText
      let text = defaultText;
      if (product) {
        const rawText = product.metafields?.custom?.reviews_text;
        const val = typeof rawText === 'object' && rawText !== null ? rawText.value : rawText;
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          text = String(val).trim();
        }
      }

      const percentage = (rating / 5.0) * 100;
      const starPaths = [];
      for (let i = 1; i <= 5; i++) {
        const offset = (i - 1) * 22;
        starPaths.push(`<path d="M${offset + 10.0078} 1.00098L${offset + 12.0361} 5.81934C${offset + 12.328} 6.51225 ${offset + 12.9837} 6.97813 ${offset + 13.7275} 7.03711L${offset + 18.9854} 7.45312L${offset + 14.9893} 10.835H${offset + 14.9883}C${offset + 14.4157} 11.3197 ${offset + 14.1618} 12.086 ${offset + 14.3389} 12.8193L${offset + 15.5615} 17.8848C${offset + 15.5599} 17.8857 ${offset + 15.5589} 17.8872 ${offset + 15.5576} 17.8877C${offset + 15.5559} 17.8869 ${offset + 15.5532} 17.8858 ${offset + 15.5498} 17.8838L${offset + 11.041} 15.1631C${offset + 10.4014} 14.7772 ${offset + 9.59865} 14.7772 ${offset + 8.95898} 15.1631L${offset + 4.4502} 17.8838C${offset + 4.44677} 17.8858 ${offset + 4.4441} 17.8869 ${offset + 4.44238} 17.8877C${offset + 4.44085} 17.8871 ${offset + 4.43936} 17.8859 ${offset + 4.4375} 17.8848L${offset + 5.66113} 12.8193C${offset + 5.8271} 12.1321 ${offset + 5.61431} 11.416 ${offset + 5.11523} 10.9297L${offset + 5.01172} 10.835L${offset + 1.01367} 7.45312L${offset + 6.27246} 7.03711C${offset + 7.01627} 6.97814 ${offset + 7.67205} 6.51246 ${offset + 7.96387} 5.81934L${offset + 9.99121} 1.00098C${offset + 9.99362} 1.00054 ${offset + 9.99658} 1 ${offset + 10} 1C${offset + 10.003} 1 ${offset + 10.0056} 1.00062 ${offset + 10.0078} 1.00098Z" fill="currentColor" stroke="currentColor" stroke-width="2"/>`);
      }
      const pathsHtml = starPaths.join("");

      return `
        <div class="popular-products__rating">
          <span class="popular-products__stars d-flex" aria-hidden="true" style="position: relative; width: 108px; height: 19px; color: #D8D8D8;">
            <svg width="108" height="19" viewBox="0 0 108 19" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
              ${pathsHtml}
            </svg>
            <span style="position: absolute; top: 0; left: 0; width: ${percentage}%; height: 100%; overflow: hidden; color: #000000; display: flex;">
              <svg width="108" height="19" viewBox="0 0 108 19" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0; width: 108px; display: block;">
                ${pathsHtml}
              </svg>
            </span>
          </span>
          <span class="popular-products__reviews text-tirty fw-normal">${count} ${escapeHtml(text)}</span>
        </div>
      `;
    }

    function renderProductCard(product, panel) {
      const productUrl = getProductUrl(product);
      const image = getProductImage(product);
      const variant = getFirstAvailableVariant(product);
      const uid = `${panel.id}-${product.id || product.handle}`;
      const compareAt = product.compare_at_price || product.compare_at_price_max || 0;
      const price = typeof product.price === "number" ? product.price : product.price_min || 0;

      let weightTagHtml = "<div></div>";
      if (product && product.metafields) {
        const rawWeight = product.metafields.custom?.weight_tag;
        const val = typeof rawWeight === "object" && rawWeight !== null ? rawWeight.value : rawWeight;
        if (val !== undefined && val !== null && String(val).trim() !== "") {
          weightTagHtml = `
            <div class="popular-products__tag">
              <span class="popular-products__tag-value text-tirty fw-normal">
                ${escapeHtml(String(val).trim())}
              </span>
            </div>
          `;
        }
      }

      return `
        <div class="swiper-slide">
          <div class="popular-products__card">
            <a class="popular-products__media" href="${productUrl}">
              ${
                image
                  ? `<img
                      src="${image}"
                      alt="${escapeHtml(product.title || "")}"
                      loading="lazy"
                      width="900"
                      height="900"
                    >`
                  : ""
              }
            </a>

            <div class="popular-products__card-content">
              <div class="popular-products__meta">
                <a class="popular-products__name h3-second" href="${productUrl}">
                  ${escapeHtml(product.title || "")}
                </a>

                <div class="popular-products__price">
                  ${
                    compareAt > price
                      ? `
                        <span class="popular-products__price-current h3-second">
                          ${formatMoney(price)}
                        </span>
                        <span class="popular-products__price-compare text-large">
                          ${formatMoney(compareAt)}
                        </span>
                      `
                      : `
                        <span class="popular-products__price-current h3-second">
                          ${formatMoney(price)}
                        </span>
                      `
                  }
                </div>

                ${buildDynamicRatingHtml(product, panel, uid)}

                <hr style="border: 0; border-top: 1px solid rgba(0,0,0,0.10); margin: 16px 0; opacity:1;">
              </div>

              <div class="popular-products__card-bottom">
                ${
                  variant && variant.available
                    ? `
                      <form
                        method="post"
                        action="/cart/add"
                        class="popular-products__cta-form w-100"
                        data-product-card-atc
                      >
                        <input type="hidden" name="id" value="${variant.id}">
                        <input type="hidden" name="quantity" value="1">

                        <button type="submit" class="cta cta-primary w-100 d-flex align-items-center justify-content-center gap-2" aria-label="Add to cart">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6.875 16.2222C7.37228 16.2222 7.84919 16.4212 8.20083 16.7755C8.55246 17.1297 8.75 17.6101 8.75 18.1111C8.75 18.6121 8.55246 19.0925 8.20083 19.4468C7.84919 19.801 7.37228 20 6.875 20C6.37772 20 5.90081 19.801 5.54917 19.4468C5.19754 19.0925 5 18.6121 5 18.1111C5 17.6101 5.19754 17.1297 5.54917 16.7755C5.90081 16.4212 6.37772 16.2222 6.875 16.2222ZM6.875 16.2222H17.1875M6.875 16.2222V3H5M17.1875 16.2222C17.6848 16.2222 18.1617 16.4212 18.5133 16.7755C18.865 17.1297 19.0625 17.6101 19.0625 18.1111C19.0625 18.6121 18.865 19.0925 18.5133 19.4468C18.1617 19.801 17.6848 20 17.1875 20C16.6902 20 16.2133 19.801 15.8617 19.4468C15.51 19.0925 15.3125 18.6121 15.3125 18.1111C15.3125 17.6101 15.51 17.1297 15.8617 16.7755C16.2133 16.4212 16.6902 16.2222 17.1875 16.2222ZM6.875 4.88889L20 5.83333L19.0625 12.4444H6.875" stroke="white" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
                          </svg>
                          <span>${escapeHtml(panel.dataset.addToCartText || "In winkelwagen")}</span>
                        </button>
                      </form>
                    `
                    : `
                      <button
                        class="cta cta-primary w-100 d-flex align-items-center justify-content-center gap-2 is-disabled"
                        type="button"
                        disabled
                        aria-label="Sold out"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M6.875 16.2222C7.37228 16.2222 7.84919 16.4212 8.20083 16.7755C8.55246 17.1297 8.75 17.6101 8.75 18.1111C8.75 18.6121 8.55246 19.0925 8.20083 19.4468C7.84919 19.801 7.37228 20 6.875 20C6.37772 20 5.90081 19.801 5.54917 19.4468C5.19754 19.0925 5 18.6121 5 18.1111C5 17.6101 5.19754 17.1297 5.54917 16.7755C5.90081 16.4212 6.37772 16.2222 6.875 16.2222ZM6.875 16.2222H17.1875M6.875 16.2222V3H5M17.1875 16.2222C17.6848 16.2222 18.1617 16.4212 18.5133 16.7755C18.865 17.1297 19.0625 17.6101 19.0625 18.1111C19.0625 18.6121 18.865 19.0925 18.5133 19.4468C18.1617 19.801 17.6848 20 17.1875 20C16.6902 20 16.2133 19.801 15.8617 19.4468C15.51 19.0925 15.3125 18.6121 15.3125 18.1111C15.3125 17.6101 15.51 17.1297 15.8617 16.7755C16.2133 16.4212 16.6902 16.2222 17.1875 16.2222ZM6.875 4.88889L20 5.83333L19.0625 12.4444H6.875" stroke="white" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
                        </svg>
                        <span>Uitverkocht</span>
                      </button>
                    `
                }
              </div>
            </div>
          </div>
        </div>
      `;
    }

    function renderEmpty(panel, message) {
      const wrapper = panel.querySelector("[data-popular-wrapper]");
      if (!wrapper) return;

      wrapper.innerHTML = `
        <div class="popular-products__empty container">
          <p class="mb-0">${escapeHtml(message)}</p>
        </div>
      `;
    }

    async function getHandlesFromCartOrUrl(panel) {
      const handlesFromUrl = getQueryParam("product_handles");
      if (handlesFromUrl) {
        return handlesFromUrl
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      const cart = await fetchCart();
      if (cart?.items?.length) {
        return cart.items
          .map((item) => item.handle)
          .filter(Boolean);
      }

      const fallback = panel.dataset.fallbackProductHandle;
      return fallback ? [fallback] : [];
    }

    async function loadRelatedProducts(panel) {
      const limit = Number(panel.dataset.limit || 8);
      const intent = panel.dataset.relatedIntent || "related";

      const handleFromUrl =
        getQueryParam("product_handle") ||
        getQueryParam("handle") ||
        panel.dataset.fallbackProductHandle;

      if (!handleFromUrl) return [];

      const baseProduct = await fetchProductByHandle(handleFromUrl);
      if (!baseProduct?.id) return [];

      const recs = await fetchRecommendations(baseProduct.id, limit, intent);
      return uniqueProducts(recs).slice(0, limit);
    }

    async function loadCartBasedProducts(panel) {
      const limit = Number(panel.dataset.limit || 8);
      const intent = panel.dataset.relatedIntent || "related";
      const baseHandles = await getHandlesFromCartOrUrl(panel);

      if (!baseHandles.length) return [];

      const sourceHandleSet = new Set(baseHandles);
      const allRecommendations = [];

      for (const handle of baseHandles) {
        const product = await fetchProductByHandle(handle);
        if (!product?.id) continue;

        const recs = await fetchRecommendations(product.id, limit, intent);
        allRecommendations.push(...recs);
      }

      return uniqueProducts(allRecommendations)
        .filter((product) => !sourceHandleSet.has(product.handle))
        .slice(0, limit);
    }

    async function hydrateDynamicPanel(panel) {
      const wrapper = panel.querySelector("[data-popular-wrapper]");
      if (!wrapper) return;

      const sourceType = panel.dataset.sourceType;
      const emptyMessage = panel.dataset.emptyMessage || "No products found.";

      let products = [];

      try {
        if (sourceType === "related") {
          products = await loadRelatedProducts(panel);
        } else if (sourceType === "cart") {
          products = await loadCartBasedProducts(panel);
        }
      } catch (error) {
        console.error("Popular products dynamic load error:", error);
      }

      if (!products.length) {
        renderEmpty(panel, emptyMessage);
        return;
      }

      wrapper.innerHTML = products.map((product) => renderProductCard(product, panel)).join("");
    }

    const isTabbedSection = tabs.length > 0;

    if (isTabbedSection) {
      const firstActive = section.querySelector(".popular-products__panel.is-active");
      if (firstActive) initSwiper(firstActive);

      tabs.forEach((btn) => {
        btn.addEventListener("click", () => {
          const target = btn.getAttribute("data-tab-target");
          if (!target) return;
          setActiveTab(target);
        });
      });

      return;
    }

    const singlePanel = section.querySelector(".popular-products__panel.is-active, [data-popular-root]");
    if (!singlePanel) return;

    const sourceType = singlePanel.dataset.sourceType;

    if (!sourceType || sourceType === "collection") {
      initSwiper(singlePanel);
      return;
    }

    hydrateDynamicPanel(singlePanel).then(() => {
      initSwiper(singlePanel);
      requestAnimationFrame(() => {
        const el = singlePanel.querySelector(".js-popular-swiper");
        if (el && el._swiper) el._swiper.update();
      });
    });
  });
}

export function initRelatedProductsSwiper(scope = document) {
  const sections = scope.querySelectorAll("[data-related-products-root]");
  if (!sections.length) return;

  sections.forEach((section) => {
    const el = section.querySelector(".js-popular-swiper");
    const fill = section.querySelector(".js-popular-fill");

    if (!el) return;

    if (el._swiper) {
      el._swiper.destroy(true, true);
      el._swiper = null;
    }

    let data = {};

    try {
      data = JSON.parse(el.getAttribute("data-options") || "{}");
    } catch (_) {
      data = {};
    }

    function updateFill(swiper) {
      if (!fill) return;

      const progress =
        swiper && typeof swiper.progress === "number" ? swiper.progress : 0;
      const clamped = Math.max(0, Math.min(1, progress));

      fill.style.transform = `scaleX(${0.2 + clamped * 0.8})`;
    }

    el._swiper = new Swiper(el, {
      ...data,
      navigation: {
        nextEl: section.querySelector(".popular-next"),
        prevEl: section.querySelector(".popular-prev"),
      },
      on: {
        init() {
          updateFill(this);
        },
        slideChange() {
          updateFill(this);
        },
        setTranslate() {
          updateFill(this);
        },
        transitionEnd() {
          updateFill(this);
        },
      },
    });

    requestAnimationFrame(() => {
      if (el._swiper) {
        el._swiper.update();
        updateFill(el._swiper);
      }
    });
  });
}

document.addEventListener('submit', async function (event) {
  const form = event.target.closest('[data-product-card-atc]');
  if (!form) return;

  event.preventDefault();

  const submitButton = form.querySelector('.popular-products__cta');
  const idInput = form.querySelector('input[name="id"]');
  const qtyInput = form.querySelector('input[name="quantity"]');

  if (!idInput) return;

  const variantId = Number(idInput.value);
  const quantity = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;

  if (!variantId) return;

  if (submitButton) {
    submitButton.disabled = true;
    submitButton.classList.add('is-loading');
  }

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error(`Add to cart failed: ${response.status}`);
    }

    const btn = document.querySelector('#js-open-mini-cart, [data-open-mini-cart]');
    if (btn) btn.click();
    else document.dispatchEvent(new CustomEvent('cart:refresh'));

  } catch (error) {
    console.error('Popular products add error:', error);
  } finally {
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.classList.remove('is-loading');
    }
  }
});

document.addEventListener("DOMContentLoaded", () => {
  initPopularProductsTabs();
  initRelatedProductsSwiper();
});

document.addEventListener("related-products:hydrated", () => {
  initRelatedProductsSwiper();
});